import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';

type Props = {
  value: string;
  /** Matches the existing market feed contract: previous value minus current value. */
  priceDiff?: number | string | null;
  fitValue?: boolean;
};

const FIT_SAFETY_FACTOR = 0.96;
const FALLBACK_MAX_FONT_SIZE = 15;

const MarketMovement: React.FC<Props> = ({
  value,
  priceDiff,
  fitValue = false,
}) => {
  const valueRef = useRef<HTMLSpanElement>(null);
  const difference = Number(priceDiff);
  const hasDifference = priceDiff != null && Number.isFinite(difference);
  const movedUp = hasDifference && difference < 0;
  const movedDown = hasDifference && difference > 0;

  const fit = useCallback(() => {
    const element = valueRef.current;
    if (!element) return;
    element.style.removeProperty('font-size');
    if (!fitValue) return;

    const availableWidth = element.clientWidth;
    const requiredWidth = element.scrollWidth;
    if (availableWidth <= 0 || requiredWidth <= availableWidth) return;

    const inheritedFontSize = Number.parseFloat(
      window.getComputedStyle(element).fontSize,
    );
    const maxFontSize = Number.isFinite(inheritedFontSize)
      ? inheritedFontSize
      : FALLBACK_MAX_FONT_SIZE;
    const fittedFontSize =
      Math.floor(
        maxFontSize *
          (availableWidth / requiredWidth) *
          FIT_SAFETY_FACTOR *
          1000,
      ) / 1000;
    element.style.fontSize = `${fittedFontSize}px`;
  }, [fitValue]);

  useLayoutEffect(() => {
    fit();
    if (!fitValue || !valueRef.current) return undefined;

    const observer = new ResizeObserver(fit);
    observer.observe(valueRef.current.parentElement || valueRef.current);
    return () => observer.disconnect();
  }, [fit, fitValue, value]);

  return (
    <span
      className="markets-v4-market-movement"
      data-fit-value={fitValue || undefined}
    >
      <span
        ref={valueRef}
        className="markets-v4-market-movement__value"
        title={value}
      >
        {value}
      </span>
      {movedUp && (
        <CaretUpOutlined
          className="markets-v4-market-movement__arrow is-up"
          aria-label="Increased"
        />
      )}
      {movedDown && (
        <CaretDownOutlined
          className="markets-v4-market-movement__arrow is-down"
          aria-label="Decreased"
        />
      )}
    </span>
  );
};

export default React.memo(MarketMovement);
